import { Component, ReactNode, ErrorInfo } from 'react';
import { clearTokensAndRedirect } from '@/utils/jwt';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component to catch and handle token-related errors
 * Automatically logs out users when critical token errors occur
 */
export class TokenErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to the console
    console.error('TokenErrorBoundary caught an error:', error, errorInfo);

    // Check if the error is token-related
    const isTokenError = this.isTokenRelatedError(error);

    if (isTokenError) {
      console.error('Token error detected, clearing tokens and redirecting to login');
      
      // Clear tokens and redirect to login
      // Use setTimeout to ensure error boundary state is updated first
      setTimeout(() => {
        clearTokensAndRedirect();
      }, 100);
    }
  }

  /**
   * Determine if the error is related to tokens
   */
  private isTokenRelatedError(error: Error): boolean {
    const errorMessage = error.message?.toLowerCase() || '';
    const errorName = error.name?.toLowerCase() || '';
    
    const tokenErrorKeywords = [
      'token',
      'jwt',
      'unauthorized',
      'authentication',
      'expired',
      'invalid token',
      'malformed',
      'decode',
    ];

    return tokenErrorKeywords.some(
      keyword => 
        errorMessage.includes(keyword) || 
        errorName.includes(keyword)
    );
  }

  render() {
    if (this.state.hasError) {
      const isTokenError = this.state.error ? this.isTokenRelatedError(this.state.error) : false;

      if (isTokenError) {
        // Show a minimal message while redirecting
        return (
          <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-800 mb-4">
                Session Expired
              </h1>
              <p className="text-gray-600 mb-4">
                Your session has expired. Redirecting to login...
              </p>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          </div>
        );
      }

      // For non-token errors, show a generic error message with retry option
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center max-w-md p-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-4">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-600 text-white cursor-pointer rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh Page
            </button>
            {this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Error details
                </summary>
                <pre className="mt-2 p-4 bg-gray-100 rounded text-xs overflow-auto">
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
